import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { ResourceEmpty } from "../../utils/ResourceEmpty";

export const DrawingsStackList = ({ project, filters = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
      };

      // Add project filter if project is provided
      if (project?.id) {
        params.project_id = project.id;
      }

      // Add any additional filters
      filters.forEach((filter) => {
        if (filter.name && filter.value) {
          params[filter.name] = filter.value;
        }
      });

      const response = await axios({
        method: "get",
        url: "/drawings/",
        params: params,
      });
      setDrawings(response.data.data || []);
    } catch (err) {
      console.error("Error fetching drawings:", err);
      setError(true);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery, project?.id]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("drawings.delete_confirm"))) {
      return;
    }

    try {
      await axios({
        method: "delete",
        url: `/drawings/${id}`,
      });
      toast.success(t("drawings.deleted"));
      fetchData();
    } catch (err) {
      console.error("Error deleting drawing:", err);
      toast.error(t("drawings.delete_error"));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError />;
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("drawings.list")}
        </h2>
        <button
          onClick={() =>
            navigate("/dashboard/drawings/new", {
              state: { project: project },
            })
          }
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {t("drawings.new")}
        </button>
      </div>

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder={t("drawings.search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Drawings List */}
      {drawings.length === 0 ? (
        <ResourceEmpty message={t("drawings.no_results")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drawings.map((drawing) => (
            <div
              key={drawing.id}
              className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-base font-semibold text-gray-900">
                  {drawing.title}
                </h3>
                <div className="flex space-x-2">
                  <Link
                    to={`/dashboard/drawings/${drawing.id}`}
                    className="text-blue-600 hover:text-blue-800"
                    title={t("View")}
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                  <Link
                    to={`/dashboard/drawings/${drawing.id}/edit`}
                    className="text-gray-600 hover:text-gray-800"
                    title={t("Edit")}
                  >
                    <PencilIcon className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => handleDelete(drawing.id)}
                    className="text-red-600 hover:text-red-800"
                    title={t("Delete")}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              {drawing.description && (
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {drawing.description}
                </p>
              )}
              {(drawing.floor || drawing.house) && (
                <p className="text-xs text-gray-500 mt-2">
                  {drawing.floor && `${t("drawings.floor")}: ${drawing.floor}`}
                  {drawing.floor && drawing.house && " | "}
                  {drawing.house && `${t("drawings.house")}: ${drawing.house}`}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

